import { Button, Header, MailIcon, Paragraph, TextInput, notification } from "@datagame/ui";
import { Formik, Form, Field } from "formik";
import { object, string } from "yup";
import { useMutation } from "@apollo/client";
import { useTranslation } from "react-i18next";
import { useRef } from "react";
// eslint-disable-next-line
import ReCAPTCHA from "react-google-recaptcha";

import { UserRequestPasswordResetMutationVariables } from "../../../../gql/graphql";
import { USER_REQUEST_PASSWORD_RESET } from "../../../../graphql/mutations/UserMutations";
import { FieldWrapper } from "../../../shared/ui/FieldWrapper";
import { ErrorMessage } from "../../../shared/ui/ErrorMessage";

import styles from "./Step.module.scss";

const VITE_RECAPTCHA_SITE_KEY = import.meta.env.VITE_RECAPTCHA_SITE_KEY;

export interface ForgotPasswordValues {
  email: string;
  token: string;
}

export interface StepProps {
  changeStep: (step: number) => void;
}

export const Step1 = ({ changeStep }: StepProps) => {
  const { t } = useTranslation("account");
  const recaptcha = useRef<ReCAPTCHA>(null);

  const [resetPassword, { error }] = useMutation(USER_REQUEST_PASSWORD_RESET);

  const initialValues: ForgotPasswordValues = {
    email: "",
    token: "",
  };

  const handleSubmit = (values: UserRequestPasswordResetMutationVariables) => {
    const captchaValue = recaptcha?.current?.getValue();
    recaptcha?.current?.reset();

    if (!captchaValue) {
      notification.danger({
        title: "Please verify the reCAPTCHA!",
        description: "Please verify the reCAPTCHA!",
      });
    }
    if (captchaValue) {
      resetPassword({
        variables: {
          email: values.email,
          token: captchaValue,
        },
        onCompleted: () => {
          localStorage.setItem("email", values.email);
          changeStep(2);
        },
      });
    }
  };

  const validationSchema = object({
    email: string().email().required(),
  });

  return (
    <>
      <Header variant="h1" className={styles.header}>
        {t("forgotPassword.step1.header1")}{" "}
        <span className={styles.orangeText}>{t("forgotPassword.step1.header2")}</span>
      </Header>
      <Paragraph color="secondary" className={styles.description}>
        {t("forgotPassword.description")}
      </Paragraph>
      <Formik
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validationSchema={validationSchema}
        validateOnBlur={false}
        validateOnChange={false}
      >
        {({ errors }) => (
          <Form className={styles.formWrapper}>
            {error && <ErrorMessage>{error.message}</ErrorMessage>}
            <div className={styles.fieldsWrapper}>
              <FieldWrapper label="Email address" name="email">
                <Field
                  as={TextInput}
                  error={errors.email}
                  name="email"
                  icon={<MailIcon />}
                  placeholder="Please enter your email..."
                  type="email"
                />
              </FieldWrapper>
            </div>
            <ReCAPTCHA ref={recaptcha} sitekey={VITE_RECAPTCHA_SITE_KEY} />

            <Button className={styles.button} type="submit" size="large">
              {t("sendCode")}
            </Button>
          </Form>
        )}
      </Formik>
    </>
  );
};
